// bridge.js — single entry for all native <-> JS communication

function rid() {
    return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

const SCHEMA_VERSION = 1

class NativeBridge {
    constructor() {
        this._pending = new Map()
        this._listeners = new Map()
        this._sender = null
        this._timeoutMs = 2500

        // Logging
        this._logLevel = 'info' // 'debug' | 'info' | 'warn' | 'error' | 'none'
        this._logger = console
        this._platform = 'unknown'
        this._transport = 'unknown'

        // Detect transport/platform (now with browser fallback)
        const det = this._detectTransport()
        this._sender = det.sender
        this._platform = det.platform
        this._transport = det.transport

        this._log('info', '🌉 Bridge initialized', {
            platform: this._platform,
            transport: this._transport,
            hasSender: !!this._sender,
        })

        // Inbound from native via CustomEvent('bridge', { detail })
        window.addEventListener('bridge', (e) => {
            this._log('debug', '↩︎ inbound CustomEvent bridge', e.detail)
            this._receive(e.detail)
        })

        // Inbound from native via direct call
        window.NativeBridge = window.NativeBridge || {}
        window.NativeBridge.receive = (msg) => {
            this._log('debug', '↩︎ inbound NativeBridge.receive', msg)
            this._receive(msg)
        }
    }

    configure(opts = {}) {
        if (typeof opts.sender === 'function') this._sender = opts.sender
        if (typeof opts.timeoutMs === 'number') this._timeoutMs = opts.timeoutMs
        if (typeof opts.logLevel === 'string') this._logLevel = opts.logLevel
        if (opts.logger && typeof opts.logger.log === 'function') this._logger = opts.logger

        this._log('info', '⚙️ Bridge configured', {
            timeoutMs: this._timeoutMs,
            logLevel: this._logLevel,
            hasSender: !!this._sender,
        })
    }

    request(action, payload = null, opts = {}) {
        this._ensureSender()

        const id = rid()
        const envelope = {
            id,
            v: SCHEMA_VERSION,
            type: 'request',
            action,
            payload,
            meta: { ts: Date.now(), origin: 'js' },
        }

        const timeoutMs = typeof opts.timeoutMs === 'number' ? opts.timeoutMs : this._timeoutMs
        this._log('info', `→ request ${action}`, { id, timeoutMs, payload })

        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                this._pending.delete(id)
                const err = this._err('TIMEOUT', `Request timed out after ${timeoutMs}ms`, envelope)
                this._log('warn', `⏱ timeout ${action}`, { id, timeoutMs })
                reject(err)
            }, timeoutMs)

            this._pending.set(id, { resolve, reject, timer })
            this._send(envelope)
        })
    }

    emit(action, payload = null) {
        this._ensureSender()

        const envelope = {
            id: rid(),
            v: SCHEMA_VERSION,
            type: 'event',
            action,
            payload,
            meta: { ts: Date.now(), origin: 'js' },
        }
        this._log('info', `→ event ${action}`, { id: envelope.id, payload })
        this._send(envelope)
    }

    on(action, handler) {
        if (!this._listeners.has(action)) this._listeners.set(action, new Set())
        this._listeners.get(action).add(handler)
        this._log('debug', `👂 subscribed ${action}`, {
            listeners: this._listeners.get(action).size,
        })
        return () => this.off(action, handler)
    }

    off(action, handler) {
        const set = this._listeners.get(action)
        if (set) {
            set.delete(handler)
            this._log('debug', `🙉 unsubscribed ${action}`, { listeners: set.size })
        }
    }

    get platform() {
        return this._platform
    }
    get transport() {
        return this._transport
    }
    get logLevel() {
        return this._logLevel
    }

    respond({ id, action, payload, error }) {
        const envelope = {
            id,
            v: SCHEMA_VERSION,
            type: 'response',
            action,
            payload: payload ?? null,
            error: error || undefined,
            meta: { ts: Date.now(), origin: 'native' },
        }
        this._log('debug', `↩︎ manual respond ${action}`, { id, payload, error })
        this._receive(envelope)
    }

    _ensureSender() {
        if (!this._sender) {
            const err = this._err('UNAVAILABLE', 'Native bridge sender is not available yet')
            this._log('error', '❌ no sender', err)
            throw err
        }
        return true
    }

    _send(envelope) {
        try {
            this._log('debug', '⇢ sending envelope', {
                action: envelope.action,
                type: envelope.type,
                id: envelope.id,
            })
            this._sender(envelope)
        } catch (e) {
            const err = this._err('SEND_FAILED', String(e?.message || e))
            this._log('error', '❌ send failed', err)
            throw err
        }
    }

    _receive(msg) {
        if (!msg) return

        let envelope = msg
        if (typeof msg === 'string') {
            try {
                envelope = JSON.parse(msg)
            } catch {
                /* ignore */
            }
        }
        if (!envelope || typeof envelope !== 'object') return

        this._log('debug', '⇠ received envelope', {
            action: envelope.action,
            type: envelope.type,
            id: envelope.id,
        })

        if (envelope.type === 'response' && envelope.id && this._pending.has(envelope.id)) {
            const { resolve, reject, timer } = this._pending.get(envelope.id)
            clearTimeout(timer)
            this._pending.delete(envelope.id)

            if (envelope.error) {
                this._log('warn', `↯ response error ${envelope.action}`, {
                    id: envelope.id,
                    error: envelope.error,
                })
                reject(
                    this._err(
                        envelope.error.code || 'NATIVE_FAILURE',
                        envelope.error.message || 'Native error',
                        envelope,
                    ),
                )
            } else {
                this._log('info', `↤ response ${envelope.action}`, {
                    id: envelope.id,
                    payload: envelope.payload,
                })
                resolve(envelope.payload)
            }
            return
        }

        this._emitLocal(envelope)
    }

    _emitLocal(envelope) {
        const set = this._listeners.get(envelope.action)
        if (set && set.size) {
            this._log('debug', `📣 emit local ${envelope.action}`, { listeners: set.size })
            set.forEach((fn) => {
                try {
                    fn(envelope.payload, envelope)
                } catch {
                    /* empty */
                }
            })
        } else {
            this._log('debug', `📭 no listeners for ${envelope.action}`)
        }
    }

    _err(code, message, envelope) {
        const e = new Error(message)
        e.code = code
        if (envelope) e.envelope = envelope
        return e
    }

    _detectTransport() {
        // iOS WKWebView
        const ios = window.webkit && window.webkit.messageHandlers
        if (ios && ios.bridge && typeof ios.bridge.postMessage === 'function') {
            return {
                sender: (envelope) => ios.bridge.postMessage(envelope),
                platform: 'ios',
                transport: 'wkwebview.messageHandlers.bridge',
            }
        }

        // Android patterns
        if (window.Android && typeof window.Android.postMessage === 'function') {
            return {
                sender: (envelope) => window.Android.postMessage(JSON.stringify(envelope)),
                platform: 'android',
                transport: 'android.jsinterface.Android.postMessage',
            }
        }
        if (
            window.Android &&
            window.Android.bridge &&
            typeof window.Android.bridge.postMessage === 'function'
        ) {
            return {
                sender: (envelope) => window.Android.bridge.postMessage(JSON.stringify(envelope)),
                platform: 'android',
                transport: 'android.jsinterface.Android.bridge.postMessage',
            }
        }
        if (window.android && typeof window.android.postMessage === 'function') {
            return {
                sender: (envelope) => window.android.postMessage(JSON.stringify(envelope)),
                platform: 'android',
                transport: 'android.jsinterface.android.postMessage',
            }
        }

        // --- Browser fallback (dev loopback) ---
        const isBrowser =
            typeof window !== 'undefined' &&
            typeof document !== 'undefined' &&
            typeof navigator !== 'undefined'

        if (isBrowser) {
            // Use an arrow fn to capture 'this'
            const sender = (envelope) => {
                // Expose outbound traffic for tests/devtools
                try {
                    window.dispatchEvent(
                        new CustomEvent('bridge-dev-outbound', { detail: envelope }),
                    )
                } catch {
                    /* empty */
                }

                // For requests, simulate an immediate UNAVAILABLE response so Promises reject quickly
                if (envelope.type === 'request') {
                    const response = {
                        id: envelope.id,
                        v: SCHEMA_VERSION,
                        type: 'response',
                        action: envelope.action,
                        payload: null,
                        error: { code: 'UNAVAILABLE', message: 'No native bridge: browser mode' },
                        meta: { ts: Date.now(), origin: 'native' },
                    }
                    // Reuse the normal receive path
                    setTimeout(() => this._receive(response), 0)
                }

                // For events, do nothing else (already observable via bridge-dev-outbound)
            }

            return { sender, platform: 'browser', transport: 'dev.loopback' }
        }

        return { sender: null, platform: 'unknown', transport: 'none' }
    }

    _log(level, msg, meta) {
        if (!this._shouldLog(level)) return
        const prefix = `[Bridge/${this._platform}]`
        try {
            switch (level) {
                case 'debug':
                    this._logger.log(prefix, msg, meta ?? '')
                    break
                case 'info':
                    this._logger.log(prefix, msg, meta ?? '')
                    break
                case 'warn':
                    this._logger.warn(prefix, msg, meta ?? '')
                    break
                case 'error':
                    this._logger.error(prefix, msg, meta ?? '')
                    break
            }
        } catch {
            /* empty */
        }
    }

    _shouldLog(level) {
        const order = { debug: 10, info: 20, warn: 30, error: 40, none: 99 }
        return order[level] >= order[this._logLevel]
    }
}

export const bridge = new NativeBridge()
